import DonorsPanel from '../components/DonorsPanel';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const ToggleState = ({ checked }) => (
  <span className={`inline-flex min-w-[44px] justify-center rounded-full px-2 py-1 text-[11px] font-semibold ${checked ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
    {checked ? 'ON' : 'OFF'}
  </span>
);
import { 
  LogOut, Home, FileText, Image, Bell, Users, MessageSquare, 
  Plus, Trash2, Save, ExternalLink, PenSquare, Eye, Settings,
  ChevronDown, ChevronUp, Check, X, Download, ClipboardList, Loader2, BarChart3, TimerReset, MousePointerClick
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';
import SiteEditRequests from '../components/SiteEditRequests';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, logout, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  
  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  // State for all modules
  const [navigation, setNavigation] = useSearchParams();
  const editTabs = ['hero', 'banner', 'popup', 'content', 'edit-requests'];
  const manageTabs = ['analytics', 'posts', 'messages', 'donors', 'volunteers', 'petitions', 'signatures', 'comments', 'users'];
  const requestedTab = navigation.get('tab');
  const activeTab = [...editTabs, ...manageTabs].includes(requestedTab) ? requestedTab : 'posts';
  const topTab = editTabs.includes(activeTab) ? 'edit' : 'manage';
  const setActiveTab = tab => setNavigation(previous => { const next = new URLSearchParams(previous); next.set('tab', tab); return next; });
  const contentSection = navigation.get('section');
  const toggleContentSection = section => setNavigation(previous => { const next = new URLSearchParams(previous); if(next.get('section') === section) next.delete('section'); else next.set('section', section); return next; });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Content state
  const [headerConfig, setHeaderConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);
  const [noticeBar, setNoticeBar] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [bannerSettings, setBannerSettings] = useState({ text: '', link: '', enabled: false });
  const [homepagePopup, setHomepagePopup] = useState({
    enabled: false,
    headline: '',
    subheadline: '',
    body: '',
    button_text: '',
    button_link: '',
    trigger_mode: 'entry',
    delay_seconds: 0,
    frequency: 'every_visit',
    close_on_overlay: true,
    close_on_escape: true
  });
  
  // Posts state
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postFilter, setPostFilter] = useState({ status: '', category: '' });
  const [postSearch, setPostSearch] = useState('');
  
  // Petitions state
  const [petitions, setPetitions] = useState([]);
  const [selectedPetition, setSelectedPetition] = useState(null);
  
  // Signatures state
  const [signatures, setSignatures] = useState({ signatures: [], total: 0, page: 1, pages: 1 });
  const [signatureFilter, setSignatureFilter] = useState('');
  
  // Comments state
  const [comments, setComments] = useState({ comments: [], total: 0, page: 1, pages: 1 });
  const [commentStatus, setCommentStatus] = useState('pending');
  
  // Admin users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: '' });

  // Pages CMS state
  const [pages, setPages] = useState([]);

  // Admin preferences state
  const [adminPrefs, setAdminPrefs] = useState({ notify_contact_submissions: false, notify_comments: false, notify_donations: true });

  // Contact submissions / volunteers state
  const [contactSubs, setContactSubs] = useState({ submissions: [], total: 0, page: 1, pages: 1 });
  const [selectedContactSub, setSelectedContactSub] = useState(null);
  const [volunteers, setVolunteers] = useState({ volunteers: [], total: 0, sources: [] });
  const [volunteerSourceFilter, setVolunteerSourceFilter] = useState('all');

  // Analytics state
  const defaultEnd = new Date();
  const defaultStart = new Date();
  defaultStart.setDate(defaultEnd.getDate() - 29);
  const toInputDate = (date) => date.toISOString().slice(0, 10);
  const [analyticsRange, setAnalyticsRange] = useState({ start_date: toInputDate(defaultStart), end_date: toInputDate(defaultEnd) });
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [analyticsPages, setAnalyticsPages] = useState({ pages: [], landing_pages: [] });
  const [analyticsSources, setAnalyticsSources] = useState({ sources: [] });
  const [analyticsDetailView, setAnalyticsDetailView] = useState('pages');
  const [analyticsSearch, setAnalyticsSearch] = useState('');
  const [analyticsSort, setAnalyticsSort] = useState({ key: 'page_views', direction: 'desc' });

  const fetchingRef = React.useRef(false);
  const loadedTabsRef = React.useRef({});

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && activeTab !== 'edit-requests' && !fetchingRef.current) {
      fetchingRef.current = true;
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab]);

  useEffect(() => {
    if (!user || loading || loadedTabsRef.current[activeTab]) return;

    const loadActiveTabData = async () => {
      try {
        if (activeTab === 'analytics') await fetchAnalytics();
        if (activeTab === 'messages') await fetchContactSubs(1);
        if (activeTab === 'volunteers') await fetchVolunteers(volunteerSourceFilter);
        if (activeTab === 'petitions') await fetchPetitions();
        if (activeTab === 'signatures') {
          await fetchPetitions();
          await fetchSignatures();
        }
        if (activeTab === 'comments') await fetchComments(commentStatus);
        if (activeTab === 'users') await fetchAdminUsers();
        if (activeTab === 'content') await fetchPages();
        if (activeTab === 'hero') await fetchHeroSlides();
        loadedTabsRef.current[activeTab] = true;
      } catch (error) {
        console.error('Error loading tab data:', error);
      }
    };

    loadActiveTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user, loading]);

  const fetchAllData = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const [headerRes, footerRes, heroRes, noticeRes, contentRes, bannerRes, popupRes] = await Promise.all([
        axios.get(`${API_URL}/api/content/header`),
        axios.get(`${API_URL}/api/content/footer`),
        axios.get(`${API_URL}/api/admin/hero-slides`, { withCredentials: true, headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/content/notice-bar`),
        axios.get(`${API_URL}/api/content/site`),
        axios.get(`${API_URL}/api/admin/banner-settings`, { withCredentials: true, headers: authHeaders }).catch(() => ({ data: { text: '', link: '', enabled: false } })),
        axios.get(`${API_URL}/api/admin/homepage-popup`, { withCredentials: true, headers: authHeaders }).catch(() => ({ data: { enabled: false, headline: '', subheadline: '', body: '', button_text: '', button_link: '', trigger_mode: 'entry', delay_seconds: 0, frequency: 'every_visit', close_on_overlay: true, close_on_escape: true } }))
      ]);
      
      setHeaderConfig(headerRes.data);
      setFooterConfig(footerRes.data);
      setHeroSlides(heroRes.data);
      setNoticeBar(noticeRes.data);
      setSiteContent(contentRes.data);
      setBannerSettings(bannerRes.data);
      setHomepagePopup(popupRes.data);
      
      await Promise.all([
        fetchPosts(),
        fetchAdminPrefs()
      ]);
      loadedTabsRef.current.posts = true;
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/posts`, { withCredentials: true, headers: getAuthHeaders() });
      setPosts(res.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchPetitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/petitions`, { withCredentials: true, headers: getAuthHeaders() });
      setPetitions(res.data);
    } catch (error) {
      console.error('Error fetching petitions:', error);
    }
  };

  const fetchSignatures = async (petition = '') => {
    try {
      const params = petition ? `?petition=${petition}` : '';
      const res = await axios.get(`${API_URL}/api/admin/signatures${params}`, { withCredentials: true, headers: getAuthHeaders() });
      setSignatures(res.data);
    } catch (error) {
      console.error('Error fetching signatures:', error);
    }
  };

  const fetchComments = async (status = 'pending') => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/comments?status=${status}`, { withCredentials: true, headers: getAuthHeaders() });
      setComments(res.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true, headers: getAuthHeaders() });
      setAdminUsers(res.data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const fetchHeroSlides = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/hero-slides`, { withCredentials: true, headers: getAuthHeaders() });
      setHeroSlides(res.data);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
    }
  };

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/posts?post_type=page`, { withCredentials: true, headers: getAuthHeaders() });
      setPages(res.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const fetchAdminPrefs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/preferences`, { withCredentials: true, headers: getAuthHeaders() });
      setAdminPrefs(res.data);
    } catch (error) {
      console.error('Error fetching admin prefs:', error);
    }
  };

  const saveAdminPrefs = async (prefs) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/preferences`, prefs, { withCredentials: true, headers: getAuthHeaders() });
      setAdminPrefs(res.data);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
  };

  const fetchContactSubs = async (pg = 1) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/contact-submissions?page=${pg}`, { withCredentials: true, headers: getAuthHeaders() });
      setContactSubs(res.data);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    }
  };

  const fetchVolunteers = async (source = volunteerSourceFilter) => {
    try {
      const query = source && source !== 'all' ? `?source=${encodeURIComponent(source)}` : '';
      const res = await axios.get(`${API_URL}/api/admin/volunteers${query}`, { withCredentials: true, headers: getAuthHeaders() });
      setVolunteers(res.data);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchAnalytics = async (range = analyticsRange) => {
    try {
      const query = new URLSearchParams(range).toString();
      const headers = getAuthHeaders();
      const [overviewRes, pagesRes, sourcesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics/overview?${query}`, { withCredentials: true, headers }),
        axios.get(`${API_URL}/api/admin/analytics/pages?limit=200&${query}`, { withCredentials: true, headers }),
        axios.get(`${API_URL}/api/admin/analytics/sources?limit=200&${query}`, { withCredentials: true, headers }),
      ]);
      setAnalyticsOverview(overviewRes.data);
      setAnalyticsPages(pagesRes.data);
      setAnalyticsSources(sourcesRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const analyticsPresetRanges = [
    { key: '7d', label: '7D', getRange: () => { const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 6); return { start_date: toInputDate(start), end_date: toInputDate(end) }; } },
    { key: '30d', label: '30D', getRange: () => { const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 29); return { start_date: toInputDate(start), end_date: toInputDate(end) }; } },
    { key: '90d', label: '90D', getRange: () => { const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 89); return { start_date: toInputDate(start), end_date: toInputDate(end) }; } },
    { key: 'mtd', label: 'MTD', getRange: () => { const end = new Date(); const start = new Date(end.getFullYear(), end.getMonth(), 1); return { start_date: toInputDate(start), end_date: toInputDate(end) }; } },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Save handlers
  const saveHeader = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/header`, headerConfig, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Header saved');
    } catch (error) {
      toast.error('Failed to save header');
    } finally {
      setSaving(false);
    }
  };

  const saveFooter = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/footer`, footerConfig, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Footer saved');
    } catch (error) {
      toast.error('Failed to save footer');
    } finally {
      setSaving(false);
    }
  };

  const saveNoticeBar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/notice-bar`, noticeBar, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Notice bar saved');
    } catch (error) {
      toast.error('Failed to save notice bar');
    } finally {
      setSaving(false);
    }
  };

  const saveSiteContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/site-content`, siteContent, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Site content saved');
    } catch (error) {
      toast.error('Failed to save site content');
    } finally {
      setSaving(false);
    }
  };

  const saveBannerSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/banner-settings`, bannerSettings, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Banner settings saved');
    } catch (error) {
      toast.error('Failed to save banner settings');
    } finally {
      setSaving(false);
    }
  };

  const saveHomepagePopup = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/homepage-popup`, homepagePopup, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Homepage popup saved');
    } catch (error) {
      toast.error('Failed to save homepage popup');
    } finally {
      setSaving(false);
    }
  };

  // Hero slide handlers
  const addHeroSlide = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/hero-slides`, {
        title: 'New Slide',
        description: 'Add content here',
        link_text: 'LEARN MORE',
        link_url: '/',
        background_image: '',
        sort_order: heroSlides.length
      }, { withCredentials: true, headers: getAuthHeaders() });
      await fetchHeroSlides();
      toast.success('Slide added');
    } catch (error) {
      toast.error('Failed to add slide');
    }
  };

  const updateHeroSlide = async (slideId, data) => {
    try {
      await axios.put(`${API_URL}/api/admin/hero-slides/${slideId}`, data, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Slide updated');
    } catch (error) {
      toast.error('Failed to update slide');
    }
  };

  const deleteHeroSlide = async (slideId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/hero-slides/${slideId}`, { withCredentials: true, headers: getAuthHeaders() });
      setHeroSlides(heroSlides.filter(s => s.id !== slideId));
      toast.success('Slide deleted');
    } catch (error) {
      toast.error('Failed to delete slide');
    }
  };

  // Post handlers
  const createPost = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/posts`, {
        title: 'New Post',
        content: '',
        status: 'draft'
      }, { withCredentials: true, headers: getAuthHeaders() });
      await fetchPosts();
      toast.success('Post created');
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/post?id=${postId}`, { withCredentials: true, headers: getAuthHeaders() });
      await fetchPosts();
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  // Comment moderation
  const moderateComment = async (commentId, status) => {
    try {
      await axios.put(`${API_URL}/api/comments/moderate`, { comment_id: commentId, status }, { withCredentials: true, headers: getAuthHeaders() });
      await fetchComments(commentStatus);
      toast.success(`Comment ${status}`);
    } catch (error) {
      toast.error('Failed to moderate comment');
    }
  };

  // Admin user handlers
  const createAdminUser = async () => {
    if (!newAdmin.email) {
      toast.error('Email is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/users`, { email: newAdmin.email }, { withCredentials: true, headers: getAuthHeaders() });
      await fetchAdminUsers();
      toast.success('Admin created');
      setNewAdmin({ email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const deleteAdminUser = async (adminId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${adminId}`, { withCredentials: true, headers: getAuthHeaders() });
      await fetchAdminUsers();
      toast.success('Admin deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  // Export signatures
  const exportSignatures = async () => {
    try {
      const petition = signatureFilter || petitions[0]?.name;
      if (!petition) {
        toast.error('Select a petition first');
        return;
      }
      const res = await axios.get(`${API_URL}/api/admin/export?petition=${petition}`, { withCredentials: true, headers: getAuthHeaders() });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signatures-${petition}.json`;
      a.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  if (authLoading || (loading && activeTab !== 'edit-requests')) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: '#9B1E37', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  if (!user) return null;

  const currentDetailRows = analyticsDetailView === 'pages'
    ? (analyticsPages.pages || []).map((page) => ({
        label: page.page,
        search: `${page.page}`.toLowerCase(),
        page: page.page,
        page_views: page.page_views,
        sessions: page.sessions,
        avg_time_seconds: Math.round(page.avg_time_seconds || 0),
        conversions: page.conversions,
        conversion_rate: Math.round(page.conversion_rate || 0),
      }))
    : analyticsDetailView === 'sources'
      ? (analyticsSources.sources || []).map((source) => ({
          label: source.label,
          search: `${source.label} ${source.bucket}`.toLowerCase(),
          source: source.label,
          bucket: source.bucket,
          sessions: source.sessions,
          avg_session_seconds: Math.round(source.avg_session_seconds || 0),
          conversions: source.conversions,
          conversion_rate: Math.round(source.conversion_rate || 0),
        }))
      : (analyticsPages.landing_pages || []).map((page) => ({
          label: page.page,
          search: `${page.page}`.toLowerCase(),
          page: page.page,
          sessions: page.sessions,
          conversions: page.conversions,
          conversion_rate: Math.round(page.conversion_rate || 0),
        }));

  const normalizedPostSearch = postSearch.trim().toLowerCase();
  const filteredPosts = posts.filter((post) => {
    if (!normalizedPostSearch) return true;
    const searchableText = [
      post.title,
      post.slug,
      post.category,
      post.status,
      post.excerpt,
      post.meta_title,
      post.meta_description,
      post.author,
      post.content,
    ].filter(Boolean).join(' ').toLowerCase();
    return searchableText.includes(normalizedPostSearch);
  });

  const filteredAnalyticsRows = currentDetailRows
    .filter((row) => !analyticsSearch.trim() || row.search.includes(analyticsSearch.trim().toLowerCase()))
    .sort((a, b) => {
      const aVal = a[analyticsSort.key] ?? a.label ?? '';
      const bVal = b[analyticsSort.key] ?? b.label ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return analyticsSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return analyticsSort.direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

  const exportVolunteersCsv = () => {
    const columns = ['Name', 'Email', 'Phone', 'Type', 'ZIP', 'Details', 'Prometheus Synced', 'Prometheus Error', 'Submitted At'];
    const csv = [columns.join(','), ...volunteers.volunteers.map((v) => [
      `${v.first_name || ''} ${v.last_name || ''}`.trim(),
      v.email || '',
      v.phone || '',
      v.source || '',
      v.zip_code || '',
      v.message || '',
      v.crm_synced ? 'Yes' : 'No',
      v.crm_error || '',
      v.created_at || '',
    ].map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stv-volunteers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const exportAnalyticsCsv = () => {
    const configs = {
      pages: { columns: ['Page', 'Views', 'Sessions', 'Avg Time (s)', 'Conversions', 'CVR (%)'], map: (row) => [row.page, row.page_views, row.sessions, row.avg_time_seconds, row.conversions, row.conversion_rate] },
      sources: { columns: ['Source', 'Bucket', 'Sessions', 'Avg Session (s)', 'Conversions', 'CVR (%)'], map: (row) => [row.source, row.bucket, row.sessions, row.avg_session_seconds, row.conversions, row.conversion_rate] },
      landing: { columns: ['Landing Page', 'Sessions', 'Conversions', 'CVR (%)'], map: (row) => [row.page, row.sessions, row.conversions, row.conversion_rate] },
    };
    const config = configs[analyticsDetailView];
    const csv = [config.columns.join(','), ...filteredAnalyticsRows.map((row) => config.map(row).map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stv-analytics-${analyticsDetailView}-${analyticsRange.start_date}-to-${analyticsRange.end_date}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const manageItems = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'posts', label: 'Posts', icon: FileText },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'donors', label: 'Donors', icon: Users },
    { id: 'volunteers', label: 'Volunteers', icon: Users },
    { id: 'petitions', label: 'Petitions', icon: ClipboardList },
    { id: 'signatures', label: 'Signatures', icon: PenSquare },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'users', label: 'Admin Users', icon: Users }
  ];

  const editItems = [
    { id: 'hero', label: 'Hero Slides', icon: Image },
    { id: 'banner', label: 'Banner', icon: Bell },
    { id: 'popup', label: 'Homepage Popup', icon: MousePointerClick },
    { id: 'content', label: 'Site Content', icon: Settings },
    { id: 'edit-requests', label: 'Edit Requests', icon: PenSquare },
  ];

  const currentItems = topTab === 'manage' ? manageItems : editItems;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f5f5' }} data-testid="admin-page">
      <Toaster position="top-right" richColors />
      
      {/* Admin Header */}
      <header className="sticky top-0 z-50 shadow-sm" style={{ backgroundColor: '#9B1E37' }}>
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_secure-vote-homepage/artifacts/fv4hr1qo_LOGOsecurethevote_yellowMD.png"
              alt="Secure the Vote Maryland"
              className="h-10 w-auto max-w-[64px] object-contain"
            />
            <h1 className="text-xl font-bold text-white">Admin Portal</h1>
            {/* Top-level Manage / Edit toggle */}
            <div className="flex bg-white/15 rounded-full p-0.5">
              <button
                onClick={() => { setActiveTab(manageItems[0].id); }}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  topTab === 'manage' ? 'bg-white text-[#9B1E37]' : 'text-white/80 hover:text-white'
                }`}
                data-testid="top-tab-manage"
              >
                Manage
              </button>
              <button
                onClick={() => { setActiveTab(editItems[0].id); }}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  topTab === 'edit' ? 'bg-white text-[#9B1E37]' : 'text-white/80 hover:text-white'
                }`}
                data-testid="top-tab-edit"
              >
                Edit
              </button>
            </div>
            <a href="/" target="_blank" className="text-white/70 hover:text-white">
              <ExternalLink size={18} />
            </a>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="break-all text-white/80 text-sm">{user.email}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout} 
              className="text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Sub-tab Navigation */}
      <div className="border-b bg-white">
        <div className="container mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto">
            {currentItems.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#9B1E37] text-[#9B1E37]'
                    : 'border-transparent text-slate-600 hover:text-[#9B1E37]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Analytics</h2>
                <p className="text-slate-500 mt-1">First-party traffic and conversion reporting for the public site.</p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-wrap gap-2 mr-2">
                  {analyticsPresetRanges.map((preset) => (
                    <Button key={preset.key} variant="outline" onClick={() => setAnalyticsRange(preset.getRange())}>{preset.label}</Button>
                  ))}
                </div>
                <div>
                  <Label>Start</Label>
                  <Input type="date" value={analyticsRange.start_date} onChange={(e) => setAnalyticsRange({ ...analyticsRange, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>End</Label>
                  <Input type="date" value={analyticsRange.end_date} onChange={(e) => setAnalyticsRange({ ...analyticsRange, end_date: e.target.value })} />
                </div>
                <Button variant="outline" onClick={() => fetchAnalytics()}>Refresh</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
              {[
                { label: 'Sessions', value: analyticsOverview?.summary?.sessions ?? 0, icon: BarChart3 },
                { label: 'Visitors', value: analyticsOverview?.summary?.approx_visitors ?? 0, icon: Users },
                { label: 'Page Views', value: analyticsOverview?.summary?.page_views ?? 0, icon: MousePointerClick },
                { label: 'Avg Session', value: `${Math.round(analyticsOverview?.summary?.avg_session_seconds ?? 0)}s`, icon: TimerReset },
                { label: 'Conversions', value: analyticsOverview?.summary?.contacts_submitted ?? 0, icon: ClipboardList },
              ].map((item) => (
                <Card key={item.label}>
                  <CardContent className="p-5">
                    <item.icon size={18} className="mb-3 text-[#9B1E37]" />
                    <div className="text-3xl font-bold text-slate-900">{item.value}</div>
                    <div className="text-sm text-slate-500 mt-1">{item.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Pages</CardTitle>
                  <CardDescription>Most viewed public pages in the selected range.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(analyticsPages.pages || []).slice(0, 5).map((page) => (
                    <div key={page.page} className="flex items-center justify-between border rounded-lg px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">{page.page}</div>
                        <div className="text-xs text-slate-500">{page.sessions} sessions • {Math.round(page.conversion_rate || 0)}% CVR</div>
                      </div>
                      <div className="text-right text-sm text-slate-600">{page.page_views} views</div>
                    </div>
                  ))}
                  {!(analyticsPages.pages || []).length && <div className="text-sm text-slate-500">No analytics yet.</div>}
                  {!!(analyticsPages.pages || []).length && <Button variant="outline" onClick={() => setAnalyticsDetailView('pages')}>View All Pages</Button>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Traffic Sources</CardTitle>
                  <CardDescription>Referrer and UTM-based traffic attribution.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(analyticsSources.sources || []).slice(0, 5).map((source) => (
                    <div key={`${source.bucket}-${source.label}`} className="flex items-center justify-between border rounded-lg px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">{source.label}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-wide">{source.bucket}</div>
                      </div>
                      <div className="text-right text-sm text-slate-600">{source.sessions} sessions</div>
                    </div>
                  ))}
                  {!(analyticsSources.sources || []).length && <div className="text-sm text-slate-500">No traffic source data yet.</div>}
                  {!!(analyticsSources.sources || []).length && <Button variant="outline" onClick={() => setAnalyticsDetailView('sources')}>View All Sources</Button>}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Top Landing Pages</CardTitle>
                <CardDescription>First pages seen in a session.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(analyticsPages.landing_pages || []).slice(0, 5).map((page) => (
                  <div key={page.page} className="flex items-center justify-between border rounded-lg px-4 py-3">
                    <div>
                      <div className="font-medium text-slate-900">{page.page}</div>
                      <div className="text-xs text-slate-500">{page.conversions} conversions</div>
                    </div>
                    <div className="text-right text-sm text-slate-600">{page.sessions} sessions</div>
                  </div>
                ))}
                {!(analyticsPages.landing_pages || []).length && <div className="text-sm text-slate-500">No landing-page data yet.</div>}
                {!!(analyticsPages.landing_pages || []).length && <Button variant="outline" onClick={() => setAnalyticsDetailView('landing')}>View All Landing Pages</Button>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div>
                    <CardTitle>Detailed Analytics View</CardTitle>
                    <CardDescription>Inspect the full reporting table for pages, sources, or landing pages.</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant={analyticsDetailView === 'pages' ? 'default' : 'outline'} onClick={() => setAnalyticsDetailView('pages')} style={analyticsDetailView === 'pages' ? { backgroundColor: '#9B1E37' } : {}}>Pages</Button>
                    <Button variant={analyticsDetailView === 'sources' ? 'default' : 'outline'} onClick={() => setAnalyticsDetailView('sources')} style={analyticsDetailView === 'sources' ? { backgroundColor: '#9B1E37' } : {}}>Sources</Button>
                    <Button variant={analyticsDetailView === 'landing' ? 'default' : 'outline'} onClick={() => setAnalyticsDetailView('landing')} style={analyticsDetailView === 'landing' ? { backgroundColor: '#9B1E37' } : {}}>Landing Pages</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="p-4 border-b bg-slate-50/60 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      value={analyticsSearch}
                      onChange={(e) => setAnalyticsSearch(e.target.value)}
                      placeholder={analyticsDetailView === 'sources' ? 'Search sources…' : 'Search pages…'}
                      className="min-w-[240px]"
                    />
                    <select
                      value={analyticsSort.key}
                      onChange={(e) => setAnalyticsSort((prev) => ({ ...prev, key: e.target.value }))}
                      className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {analyticsDetailView === 'pages' && (
                        <>
                          <option value="page_views">Views</option>
                          <option value="sessions">Sessions</option>
                          <option value="avg_time_seconds">Avg Time</option>
                          <option value="conversions">Conversions</option>
                          <option value="conversion_rate">CVR</option>
                          <option value="page">Page</option>
                        </>
                      )}
                      {analyticsDetailView === 'sources' && (
                        <>
                          <option value="sessions">Sessions</option>
                          <option value="avg_session_seconds">Avg Session</option>
                          <option value="conversions">Conversions</option>
                          <option value="conversion_rate">CVR</option>
                          <option value="source">Source</option>
                          <option value="bucket">Bucket</option>
                        </>
                      )}
                      {analyticsDetailView === 'landing' && (
                        <>
                          <option value="sessions">Sessions</option>
                          <option value="conversions">Conversions</option>
                          <option value="conversion_rate">CVR</option>
                          <option value="page">Landing Page</option>
                        </>
                      )}
                    </select>
                    <Button variant="outline" onClick={() => setAnalyticsSort((prev) => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      {analyticsSort.direction === 'asc' ? 'Ascending' : 'Descending'}
                    </Button>
                  </div>
                  <Button variant="outline" onClick={exportAnalyticsCsv}>Export CSV</Button>
                </div>
                {analyticsDetailView === 'pages' && (
                  <AnalyticsTable
                    columns={['Page', 'Views', 'Sessions', 'Avg Time', 'Conversions', 'CVR']}
                    rows={filteredAnalyticsRows.map((page) => [page.page, page.page_views, page.sessions, `${page.avg_time_seconds}s`, page.conversions, `${page.conversion_rate}%`])}
                    emptyText="No page analytics yet."
                  />
                )}
                {analyticsDetailView === 'sources' && (
                  <AnalyticsTable
                    columns={['Source', 'Bucket', 'Sessions', 'Avg Session', 'Conversions', 'CVR']}
                    rows={filteredAnalyticsRows.map((source) => [source.source, source.bucket, source.sessions, `${source.avg_session_seconds}s`, source.conversions, `${source.conversion_rate}%`])}
                    emptyText="No source analytics yet."
                  />
                )}
                {analyticsDetailView === 'landing' && (
                  <AnalyticsTable
                    columns={['Landing Page', 'Sessions', 'Conversions', 'CVR']}
                    rows={filteredAnalyticsRows.map((page) => [page.page, page.sessions, page.conversions, `${page.conversion_rate}%`])}
                    emptyText="No landing-page analytics yet."
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Posts</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {filteredPosts.length === posts.length
                    ? `${posts.length} total post${posts.length === 1 ? '' : 's'}`
                    : `${filteredPosts.length} of ${posts.length} posts shown`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <Input
                  value={postSearch}
                  onChange={(e) => setPostSearch(e.target.value)}
                  placeholder="Search posts by title, slug, category, status, or content…"
                  className="w-full sm:w-[420px]"
                  aria-label="Search posts"
                  data-testid="admin-post-search"
                />
                <Button onClick={() => navigate('/admin/post')} style={{ backgroundColor: '#9B1E37' }} className="hover:opacity-90 whitespace-nowrap">
                  <Plus size={16} className="mr-2" />
                  New Post
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Title</th>
                      <th className="text-left p-4 font-medium text-slate-600">Category</th>
                      <th className="text-left p-4 font-medium text-slate-600">Status</th>
                      <th className="text-left p-4 font-medium text-slate-600">Date</th>
                      <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPosts.map(post => (
                      <tr key={post.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/post?id=${post.id}`)}>
                        <td className="p-4 font-medium text-[#9B1E37]">{post.title}</td>
                        <td className="p-4 text-slate-600">{post.category}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {post.status}
                          </span>
                        </td>
                        <td className="p-4 text-slate-600 text-sm">{new Date(post.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-right flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/post?id=${post.id}`)}>
                            <PenSquare size={16} className="text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deletePost(post.id)}>
                            <Trash2 size={16} className="text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {posts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-slate-500">No posts yet</td>
                      </tr>
                    )}
                    {posts.length > 0 && filteredPosts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-slate-500">
                          No posts match “{postSearch.trim()}”.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Shared request workflow; host supplies only session transport. */}
        {activeTab === 'edit-requests' && <SiteEditRequests />}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Contact Submissions</h2>
              <Button variant="outline" size="sm" onClick={() => fetchContactSubs(1)}>Refresh</Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Name</th>
                      <th className="text-left p-4 font-medium text-slate-600">Email</th>
                      <th className="text-left p-4 font-medium text-slate-600">Phone</th>
                      <th className="text-left p-4 font-medium text-slate-600">Type / Details</th>
                      <th className="text-left p-4 font-medium text-slate-600">Prometheus</th>
                      <th className="text-left p-4 font-medium text-slate-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactSubs.submissions.map(sub => (
                      <tr key={sub.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedContactSub(sub)}>
                        <td className="p-4 font-medium text-[#9B1E37]">{sub.first_name} {sub.last_name}</td>
                        <td className="p-4 text-slate-600">{sub.email}</td>
                        <td className="p-4 text-slate-600">{sub.phone || '—'}</td>
                        <td className="p-4 text-slate-600 max-w-md">
                          <div className="font-medium text-slate-800">{sub.source || 'contact'}</div>
                          {sub.zip_code && <div className="text-xs text-slate-500">ZIP: {sub.zip_code}</div>}
                          {sub.message && <div className="text-xs text-slate-500 whitespace-pre-wrap mt-1">{sub.message}</div>}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${sub.crm_synced ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {sub.crm_synced ? 'Synced' : 'Pending'}
                          </span>
                          {sub.crm_error && <div className="text-[11px] text-red-500 mt-1 max-w-[220px] truncate" title={sub.crm_error}>Sync error</div>}
                        </td>
                        <td className="p-4 text-slate-600 text-sm">{new Date(sub.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                    {contactSubs.submissions.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-slate-500">No submissions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            {contactSubs.total > 0 && (
              <p className="text-sm text-slate-500 text-center">{contactSubs.total} total submissions</p>
            )}

            {selectedContactSub && (
              <Card className="border-[#9B1E37]/20">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle>{selectedContactSub.first_name} {selectedContactSub.last_name}</CardTitle>
                      <CardDescription>Contact submission details</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedContactSub(null)}>
                      <X size={16} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><span className="text-slate-500">Email:</span> <span className="font-medium">{selectedContactSub.email}</span></div>
                    <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{selectedContactSub.phone || '—'}</span></div>
                    <div><span className="text-slate-500">Type:</span> <span className="font-medium">{selectedContactSub.source || 'contact'}</span></div>
                    <div><span className="text-slate-500">ZIP:</span> <span className="font-medium">{selectedContactSub.zip_code || '—'}</span></div>
                    <div><span className="text-slate-500">Submitted:</span> <span className="font-medium">{new Date(selectedContactSub.created_at).toLocaleString()}</span></div>
                    <div><span className="text-slate-500">Prometheus:</span> <span className="font-medium">{selectedContactSub.crm_synced ? 'Synced' : 'Pending/Error'}</span></div>
                  </div>
                  <div>
                    <div className="text-slate-500 mb-1">Message / Details</div>
                    <div className="rounded-lg border bg-slate-50 p-4 whitespace-pre-wrap text-slate-700 min-h-[80px]">
                      {selectedContactSub.message || 'No message/details were stored for this submission.'}
                    </div>
                  </div>
                  {selectedContactSub.crm_error && (
                    <div>
                      <div className="text-red-500 mb-1">Prometheus sync error</div>
                      <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-xs text-red-700 whitespace-pre-wrap">{selectedContactSub.crm_error}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Volunteers Tab */}
        {activeTab === 'donors' && <DonorsPanel apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}

        {activeTab === 'volunteers' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Volunteers</h2>
                <p className="text-sm text-slate-500 mt-1">Election judge and poll watcher/canvasser opt-ins with full submitted details.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <select
                  value={volunteerSourceFilter}
                  onChange={(e) => { setVolunteerSourceFilter(e.target.value); fetchVolunteers(e.target.value); }}
                  className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">All volunteer types</option>
                  <option value="election-judge-interest">Election Judge</option>
                  <option value="canvasser-recommendation">Poll Watcher / Canvasser</option>
                  <option value="volunteer">Volunteer</option>
                </select>
                <Button variant="outline" size="sm" onClick={() => fetchVolunteers(volunteerSourceFilter)}>Refresh</Button>
                <Button variant="outline" size="sm" onClick={exportVolunteersCsv} disabled={!volunteers.volunteers.length}>
                  <Download size={14} className="mr-2" /> Export CSV
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card><CardContent className="p-5"><div className="text-3xl font-bold text-slate-900">{volunteers.total}</div><div className="text-sm text-slate-500">Total volunteers</div></CardContent></Card>
              <Card><CardContent className="p-5"><div className="text-3xl font-bold text-slate-900">{volunteers.volunteers.filter(v => v.source === 'election-judge-interest').length}</div><div className="text-sm text-slate-500">Election judge opt-ins</div></CardContent></Card>
              <Card><CardContent className="p-5"><div className="text-3xl font-bold text-slate-900">{volunteers.volunteers.filter(v => v.source === 'canvasser-recommendation').length}</div><div className="text-sm text-slate-500">Poll watcher/canvasser opt-ins</div></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Name</th>
                      <th className="text-left p-4 font-medium text-slate-600">Contact</th>
                      <th className="text-left p-4 font-medium text-slate-600">Opt-in Type</th>
                      <th className="text-left p-4 font-medium text-slate-600">ZIP</th>
                      <th className="text-left p-4 font-medium text-slate-600">Details</th>
                      <th className="text-left p-4 font-medium text-slate-600">Prometheus</th>
                      <th className="text-left p-4 font-medium text-slate-600">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {volunteers.volunteers.map(vol => (
                      <tr key={vol.id} className="border-b align-top hover:bg-slate-50">
                        <td className="p-4 font-medium text-slate-900">{vol.first_name} {vol.last_name}</td>
                        <td className="p-4 text-sm text-slate-600">
                          <div>{vol.email}</div>
                          <div>{vol.phone || '—'}</div>
                        </td>
                        <td className="p-4 text-sm">
                          <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 font-medium">{vol.source || 'volunteer'}</span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">{vol.zip_code || '—'}</td>
                        <td className="p-4 text-sm text-slate-600 whitespace-pre-wrap max-w-xl">{vol.message || '—'}</td>
                        <td className="p-4 text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${vol.crm_synced ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {vol.crm_synced ? 'Synced' : 'Pending/Error'}
                          </span>
                          {vol.crm_error && <div className="text-[11px] text-red-500 mt-1 max-w-[260px]" title={vol.crm_error}>Sync error: {vol.crm_error.slice(0, 120)}</div>}
                        </td>
                        <td className="p-4 text-sm text-slate-600">{new Date(vol.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                    {volunteers.volunteers.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-center text-slate-500">No volunteer submissions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Petitions Tab */}
        {activeTab === 'petitions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Petitions</h2>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Name</th>
                      <th className="text-left p-4 font-medium text-slate-600">Title</th>
                      <th className="text-left p-4 font-medium text-slate-600">Signatures</th>
                      <th className="text-left p-4 font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {petitions.map(petition => (
                      <tr key={petition.id} className="border-b hover:bg-slate-50">
                        <td className="p-4 font-medium">{petition.name}</td>
                        <td className="p-4 text-slate-600">{petition.title}</td>
                        <td className="p-4">
                          <span style={{ color: '#F6BF58' }} className="font-bold">{petition.signature_count}</span>
                          {petition.goal && <span className="text-slate-400"> / {petition.goal}</span>}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            petition.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {petition.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {petitions.length === 0 && (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-slate-500">No petitions yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Signatures Tab */}
        {activeTab === 'signatures' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Signatures</h2>
              <div className="flex gap-4">
                <select
                  value={signatureFilter}
                  onChange={(e) => { setSignatureFilter(e.target.value); fetchSignatures(e.target.value); }}
                  className="px-3 py-2 border rounded-lg"
                >
                  <option value="">All Petitions</option>
                  {petitions.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
                <Button onClick={exportSignatures} variant="outline">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold" style={{ color: '#9B1E37' }}>{signatures.total}</div>
                  <div className="text-slate-600 text-sm">Total Signatures</div>
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Date</th>
                      <th className="text-left p-4 font-medium text-slate-600">Name</th>
                      <th className="text-left p-4 font-medium text-slate-600">Email</th>
                      <th className="text-left p-4 font-medium text-slate-600">Zip</th>
                      <th className="text-left p-4 font-medium text-slate-600">Petition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signatures.signatures.map((sig, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-4 text-sm">{new Date(sig.created_at).toLocaleDateString()}</td>
                        <td className="p-4 font-medium">{sig.full_name}</td>
                        <td className="p-4 text-slate-600">{sig.email}</td>
                        <td className="p-4 text-slate-600">{sig.zip_code}</td>
                        <td className="p-4 text-slate-600">{sig.petition_name}</td>
                      </tr>
                    ))}
                    {signatures.signatures.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-slate-500">No signatures yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Comments</h2>
              <div className="flex gap-2">
                {['pending', 'approved', 'rejected', 'spam'].map(status => (
                  <Button
                    key={status}
                    variant={commentStatus === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setCommentStatus(status); fetchComments(status); }}
                    style={commentStatus === status ? { backgroundColor: '#9B1E37' } : {}}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            <Card>
              <CardContent className="p-4 space-y-4">
                {comments.comments.map(comment => (
                  <div key={comment.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{comment.author_name}</div>
                        <div className="text-sm text-slate-500">{comment.author_email} · {new Date(comment.created_at).toLocaleDateString()}</div>
                        <div className="mt-2 text-slate-700">{comment.content}</div>
                      </div>
                      <div className="flex gap-2">
                        {commentStatus === 'pending' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => moderateComment(comment.id, 'approved')}>
                              <Check size={16} className="text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => moderateComment(comment.id, 'rejected')}>
                              <X size={16} className="text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {comments.comments.length === 0 && (
                  <div className="text-center text-slate-500 py-8">No {commentStatus} comments</div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Banner Tab */}
        {activeTab === 'banner' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Banner Settings</h2>
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={bannerSettings.enabled}
                      onCheckedChange={(checked) => setBannerSettings({ ...bannerSettings, enabled: checked })}
                    />
                    <Label>Enable Banner</Label>
                  </div>
                  <ToggleState checked={bannerSettings.enabled} />
                </div>
                <div className="space-y-2">
                  <Label>Banner Text</Label>
                  <Input
                    value={bannerSettings.text}
                    onChange={(e) => setBannerSettings({ ...bannerSettings, text: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Banner Link</Label>
                  <Input
                    value={bannerSettings.link}
                    onChange={(e) => setBannerSettings({ ...bannerSettings, link: e.target.value })}
                  />
                </div>
                <Button onClick={saveBannerSettings} disabled={saving} style={{ backgroundColor: '#9B1E37' }}>
                  <Save size={16} className="mr-2" />
                  Save Banner Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Homepage Popup Tab */}
        {activeTab === 'popup' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Homepage Popup</h2>
            <Card>
              <CardHeader>
                <CardTitle>Popup Settings</CardTitle>
                <CardDescription>Configure the branded popup that appears on the homepage when enabled.</CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={homepagePopup.enabled}
                      onCheckedChange={(checked) => setHomepagePopup({ ...homepagePopup, enabled: checked })}
                    />
                    <Label>Enable Homepage Popup</Label>
                  </div>
                  <ToggleState checked={homepagePopup.enabled} />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Headline</Label>
                    <Input
                      value={homepagePopup.headline}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, headline: e.target.value })}
                      placeholder="Main popup headline"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sub-headline</Label>
                    <Input
                      value={homepagePopup.subheadline}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, subheadline: e.target.value })}
                      placeholder="Short supporting line"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Body</Label>
                  <Textarea
                    rows={5}
                    value={homepagePopup.body}
                    onChange={(e) => setHomepagePopup({ ...homepagePopup, body: e.target.value })}
                    placeholder="Popup body copy"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Button Text</Label>
                    <Input
                      value={homepagePopup.button_text}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, button_text: e.target.value })}
                      placeholder="Learn More"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Button Link</Label>
                    <Input
                      value={homepagePopup.button_link}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, button_link: e.target.value })}
                      placeholder="/volunteer"
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Trigger Mode</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={homepagePopup.trigger_mode || 'entry'}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, trigger_mode: e.target.value })}
                    >
                      <option value="entry">Automatic on entry</option>
                      <option value="delay">After time delay</option>
                      <option value="exit_intent">On exit attempt</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Delay Seconds</Label>
                    <Input
                      type="number"
                      min="0"
                      max="300"
                      value={homepagePopup.delay_seconds ?? 0}
                      disabled={homepagePopup.trigger_mode !== 'delay'}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, delay_seconds: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Show Frequency</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={homepagePopup.frequency || 'every_visit'}
                      onChange={(e) => setHomepagePopup({ ...homepagePopup, frequency: e.target.value })}
                    >
                      <option value="every_visit">Every visit</option>
                      <option value="once_per_session">Once per session</option>
                      <option value="once_per_day">Once per day</option>
                    </select>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                    <Label>Close when overlay is clicked</Label>
                    <Switch
                      checked={homepagePopup.close_on_overlay !== false}
                      onCheckedChange={(checked) => setHomepagePopup({ ...homepagePopup, close_on_overlay: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                    <Label>Close with Escape key</Label>
                    <Switch
                      checked={homepagePopup.close_on_escape !== false}
                      onCheckedChange={(checked) => setHomepagePopup({ ...homepagePopup, close_on_escape: checked })}
                    />
                  </div>
                </div>
                <Button onClick={saveHomepagePopup} disabled={saving} style={{ backgroundColor: '#9B1E37' }}>
                  <Save size={16} className="mr-2" />
                  Save Homepage Popup
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Hero Slides Tab */}
        {activeTab === 'hero' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Hero Slides</h2>
              <Button onClick={addHeroSlide} style={{ backgroundColor: '#9B1E37' }}>
                <Plus size={16} className="mr-2" />
                Add Slide
              </Button>
            </div>
            {heroSlides.map((slide, index) => (
              <Card key={slide.id}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Slide {index + 1}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => deleteHeroSlide(slide.id)}>
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Headline</Label>
                      <Input
                        value={slide.title || ''}
                        onChange={(e) => {
                          const updated = [...heroSlides];
                          updated[index].title = e.target.value;
                          setHeroSlides(updated);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Background Image</Label>
                      <ImageUpload
                        value={slide.background_image || ''}
                        onChange={(url) => {
                          const updated = [...heroSlides];
                          updated[index].background_image = url;
                          setHeroSlides(updated);
                        }}
                        getToken={getToken}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Subheadline</Label>
                    <Textarea
                      value={slide.description || ''}
                      onChange={(e) => {
                        const updated = [...heroSlides];
                        updated[index].description = e.target.value;
                        setHeroSlides(updated);
                      }}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>CTA Text</Label>
                      <Input
                        value={slide.link_text || ''}
                        onChange={(e) => {
                          const updated = [...heroSlides];
                          updated[index].link_text = e.target.value;
                          setHeroSlides(updated);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CTA URL</Label>
                      <Input
                        value={slide.link_url || ''}
                        onChange={(e) => {
                          const updated = [...heroSlides];
                          updated[index].link_url = e.target.value;
                          setHeroSlides(updated);
                        }}
                      />
                    </div>
                  </div>
                  <Button onClick={() => updateHeroSlide(slide.id, slide)} variant="outline">
                    <Save size={16} className="mr-2" />
                    Save Slide
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Site Content / Pages Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Homepage Sections (left column) */}
              <div className="lg:col-span-2 space-y-4">
                <h2 className="text-2xl font-bold text-slate-900">Homepage Content</h2>

                {/* Statement of Purpose — Collapsible */}
                <Card>
                  <button
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => toggleContentSection('statement')}
                  >
                    <CardTitle className="text-base">Statement of Purpose</CardTitle>
                    <ChevronDown size={18} className="text-slate-400" />
                  </button>
                  <div id="section-statement" className={contentSection === 'statement' ? '' : 'hidden'}>
                    <CardContent className="pt-0 space-y-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={siteContent?.statement_title || ''}
                          onChange={(e) => setSiteContent({ ...siteContent, statement_title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Text</Label>
                        <Textarea
                          value={siteContent?.statement_text || ''}
                          onChange={(e) => setSiteContent({ ...siteContent, statement_text: e.target.value })}
                          rows={4}
                        />
                      </div>
                    </CardContent>
                  </div>
                </Card>

                {/* Mission — Collapsible */}
                <Card>
                  <button
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => toggleContentSection('mission')}
                  >
                    <CardTitle className="text-base">Mission</CardTitle>
                    <ChevronDown size={18} className="text-slate-400" />
                  </button>
                  <div id="section-mission" className={contentSection === 'mission' ? '' : 'hidden'}>
                    <CardContent className="pt-0 space-y-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={siteContent?.mission_title || ''}
                          onChange={(e) => setSiteContent({ ...siteContent, mission_title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Text</Label>
                        <Textarea
                          value={siteContent?.mission_text || ''}
                          onChange={(e) => setSiteContent({ ...siteContent, mission_text: e.target.value })}
                          rows={4}
                        />
                      </div>
                    </CardContent>
                  </div>
                </Card>

                {/* Who We Are — Collapsible */}
                <Card>
                  <button
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => toggleContentSection('whoweare')}
                  >
                    <CardTitle className="text-base">Who We Are</CardTitle>
                    <ChevronDown size={18} className="text-slate-400" />
                  </button>
                  <div id="section-whoweare" className={contentSection === 'whoweare' ? '' : 'hidden'}>
                    <CardContent className="pt-0 space-y-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={siteContent?.who_we_are_title || ''}
                          onChange={(e) => setSiteContent({ ...siteContent, who_we_are_title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Text</Label>
                        <Textarea
                          value={siteContent?.who_we_are_text || ''}
                          onChange={(e) => setSiteContent({ ...siteContent, who_we_are_text: e.target.value })}
                          rows={4}
                        />
                      </div>
                    </CardContent>
                  </div>
                </Card>

                <Button onClick={saveSiteContent} disabled={saving} style={{ backgroundColor: '#9B1E37' }}>
                  <Save size={16} className="mr-2" />
                  Save Homepage Content
                </Button>
              </div>

              {/* Pages CMS (right column) */}
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-slate-900">Pages</h2>
                  <Button size="sm" onClick={() => navigate('/admin/page')} style={{ backgroundColor: '#9B1E37' }}>
                    <Plus size={14} className="mr-1" />
                    New Page
                  </Button>
                </div>
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {pages.map(pg => (
                        <div
                          key={pg.id}
                          className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer group"
                          onClick={() => navigate(`/admin/page?id=${pg.id}`)}
                          data-testid={`page-${pg.slug}`}
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-slate-900 truncate">{pg.title}</div>
                            <div className="text-xs text-slate-400 truncate">/{pg.slug}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              pg.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {pg.status}
                            </span>
                            <PenSquare size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                      {pages.length === 0 && (
                        <div className="p-6 text-center text-slate-500 text-sm">No pages yet</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <p className="text-xs text-slate-400">
                  Click any page to edit with the full page builder. For complex changes, submit an <button className="underline text-[#9B1E37]" onClick={() => { setActiveTab('edit-requests'); }}>Edit Request</button>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Admin Users</h2>
            
            {/* Create Admin Form */}
            <Card>
              <CardHeader>
                <CardTitle>Add New Admin</CardTitle>
                <CardDescription>New admins will log in via email 2FA code</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    placeholder="admin@example.com"
                    data-testid="new-admin-email"
                  />
                </div>
                <Button onClick={createAdminUser} style={{ backgroundColor: '#9B1E37' }} data-testid="create-admin-btn">
                  <Plus size={16} className="mr-2" />
                  Add Admin
                </Button>
              </CardContent>
            </Card>

            {/* Admin Users List */}
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Email</th>
                      <th className="text-left p-4 font-medium text-slate-600">Created</th>
                      <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map(admin => (
                      <tr key={admin.id} className="border-b hover:bg-slate-50">
                        <td className="p-4 font-medium">
                          {admin.email}
                          {admin.email === user.email && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">You</span>
                          )}
                        </td>
                        <td className="p-4 text-slate-600 text-sm">{new Date(admin.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-right">
                          {admin.email !== user.email && (
                            <Button variant="ghost" size="sm" onClick={() => deleteAdminUser(admin.id)}>
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Your Notification Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Your Notification Preferences</CardTitle>
                <CardDescription>Choose which email notifications you receive</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <div><div className="font-medium text-sm">Donation Notifications</div><div className="text-xs text-slate-500">New gifts, monthly signups, and attempts needing attention</div></div>
                  <Switch checked={adminPrefs.notify_donations !== false} onCheckedChange={checked => saveAdminPrefs({ ...adminPrefs, notify_donations: checked })} data-testid="pref-donation-notifications" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <div>
                    <div className="font-medium text-sm">Contact Form Submissions</div>
                    <div className="text-xs text-slate-500">Get notified when someone submits the contact form</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ToggleState checked={adminPrefs.notify_contact_submissions} />
                    <Switch
                      checked={adminPrefs.notify_contact_submissions}
                      onCheckedChange={(checked) => saveAdminPrefs({ ...adminPrefs, notify_contact_submissions: checked })}
                      data-testid="pref-contact-notifications"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <div>
                    <div className="font-medium text-sm">Comment Notifications</div>
                    <div className="text-xs text-slate-500">Get notified when someone comments on a page</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ToggleState checked={adminPrefs.notify_comments} />
                    <Switch
                      checked={adminPrefs.notify_comments}
                      onCheckedChange={(checked) => saveAdminPrefs({ ...adminPrefs, notify_comments: checked })}
                      data-testid="pref-comment-notifications"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

function AnalyticsTable({ columns, rows, emptyText }) {
  if (!rows.length) {
    return <div className="p-6 text-sm text-slate-500">{emptyText}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 border-b">
          <tr>
            {columns.map((column) => (
              <th key={column} className="text-left p-4 font-medium text-slate-600 text-sm whitespace-nowrap">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b last:border-b-0 hover:bg-slate-50">
              {row.map((cell, cellIndex) => (
                <td key={`${index}-${cellIndex}`} className="p-4 text-sm text-slate-700 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminPage;
